import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { collection, getDocs, orderBy, query, doc, getDoc, updateDoc, addDoc, where } from "firebase/firestore";
import { db, auth } from "./firebase";
import { logInteraction } from "./utils/visitorTracking";
import { trackButtonClick, getAllClickStatsWithHistory } from "./utils/buttonClickTracking";
import { useAdmin } from "./hooks/useAdmin";
import ClickDetailsPopup from "./components/ClickDetailsPopup";
import RankMovementWidgets from "./components/RankMovementWidgets";
import PredictWinnerModal, { isEventPublic } from "./components/PredictWinner";
import DancerThumbnail, { preloadDancerThumbnails } from "./DancerThumbnail";
import "./styles.css";

// ── Star ranking constants (used in Stars popup) ─────────────────────────────
const STAR_DISCIPLINE_COLORS = {
  "Latin":        { bg: "#fff3e0", border: "#f57c00" },
  "Contemporary": { bg: "#e8f5e9", border: "#388e3c" },
  "Hip Hop":      { bg: "#fce4ec", border: "#c2185b" },
  "Commercial":   { bg: "#e3f2fd", border: "#1976d2" },
  "Show Dance":   { bg: "#f3e5f5", border: "#7b1fa2" },
};
const STAR_DISCIPLINE_ORDER = ["Latin", "Contemporary", "Hip Hop", "Commercial", "Show Dance"];
const STAR_AGE_ORDER = ["Adult", "Youth", "Junior II", "Junior I", "Juvenile II", "Juvenile I"];
const STAR_LATIN_DANCE_ORDER = ["Samba", "ChaChaCha", "Rumba", "Pasodoble", "Jive"];
const STAR_RANK_ICONS = ["🥇", "🥈", "🥉"];
const STAR_RANK_COLORS = ["#b8860b", "#708090", "#a0522d"];

export default function HomePage() {
  const [activeTab, setActiveTab] = useState("upcoming");
  const [rulesSubTab, setRulesSubTab] = useState("solo"); // New state for Rules sub-tabs
  const [upcomingCompetitions, setUpcomingCompetitions] = useState([]);
  const [pastCompetitions, setPastCompetitions] = useState([]);
  const [rulesContentSolo, setRulesContentSolo] = useState("");
  const [rulesContentGroup, setRulesContentGroup] = useState("");
  const [loading, setLoading] = useState(true);
  const [cancellationPopup, setCancellationPopup] = useState({ show: false, competition: null });
  const [playlistPopup, setPlaylistPopup] = useState({ show: false, competition: null });
  const [prizeMoneyPopup, setPrizeMoneyPopup] = useState({ show: false, competition: null });
  const [activePrizeTab, setActivePrizeTab] = useState(null);
  const [currentTrack, setCurrentTrack] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioCurrentTime, setAudioCurrentTime] = useState(0);
  const [audioDuration, setAudioDuration] = useState(0);
  const [activePlaylistTab, setActivePlaylistTab] = useState(null); // Active age category tab in playlist
  // Track play/download statistics (admin only)
  const [trackStats, setTrackStats] = useState({}); // { [trackUrl]: { plays: [...], downloads: [...] } }
  const [expandedStatsTrack, setExpandedStatsTrack] = useState(null);
  const audioRef = useRef(null);

  // Format seconds as m:ss
  const formatAudioTime = (seconds) => {
    if (!seconds || isNaN(seconds) || !isFinite(seconds)) return '0:00';
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${m}:${s.toString().padStart(2, '0')}`;
  };
  
  // Music popup state
  const [showMusicPopup, setShowMusicPopup] = useState(false);
  
  // Announcement banner state
  const [announcement, setAnnouncement] = useState({ enabled: false, text: "", icon: "⚠️", textSize: "medium" });

  // Admin check for click tracking
  const { isAdmin } = useAdmin();
  const navigate = useNavigate();
  
  // 🔮 Predict Winner: events keyed by homepage competition id, and the open modal
  const [predictionEvents, setPredictionEvents] = useState({});
  const [predictModal, setPredictModal] = useState(null); // competition or null
  const [predictLinkHandled, setPredictLinkHandled] = useState(false);

  // Admins see every event (incl. drafts); the public only sees published ones.
  const loadPredictionEvents = async () => {
    try {
      const col = collection(db, "predictionEvents");
      const snap = await getDocs(isAdmin ? col : query(col, where("status", "in", ["open", "closed", "settled"])));
      const map = {};
      snap.forEach(d => { map[d.id] = d.data(); });
      setPredictionEvents(map);
    } catch (e) {
      console.error("Error loading prediction events:", e);
    }
  };

  useEffect(() => {
    loadPredictionEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  // Admin: upcoming cards always get the button; everyone else only when the event is public.
  const showPredictFor = (competition) => {
    const ev = predictionEvents[competition.id];
    if (isAdmin) return competition.type === "upcoming" || !!ev;
    return isEventPublic(ev);
  };

  const predictLabel = (competition) => {
    const ev = predictionEvents[competition.id];
    if (ev && ev.status === "settled") return "🔮 Prediction Results";
    if (ev && ev.status === "closed") return "🔮 Predictions (closed)";
    return "🔮 Predict Winner";
  };

  // Notification / email links open the modal: /?predict=<competitionId>
  useEffect(() => {
    if (predictLinkHandled || loading) return;
    const wanted = new URLSearchParams(window.location.search).get("predict");
    if (!wanted) { setPredictLinkHandled(true); return; }
    const all = [...upcomingCompetitions, ...pastCompetitions];
    const match = all.find(c => c.id === wanted);
    if (match) {
      setActiveTab(match.type === "past" ? "past" : "upcoming");
      setPredictModal(match);
    }
    setPredictLinkHandled(true);
  }, [loading, upcomingCompetitions, pastCompetitions, predictLinkHandled]);

  // Stars popup state
  const [starsPopup, setStarsPopup] = useState({ show: false, competition: null });
  const [globalStarsCache, setGlobalStarsCache] = useState(null);
  const [starsLoading, setStarsLoading] = useState(false);
  const [starsActiveTab, setStarsActiveTab] = useState(null);   // "Latin|Adult" | "Contemporary" | ...
  const [starsInnerTab, setStarsInnerTab] = useState(null);     // dance name (Latin) | age (non-Latin)
  const [starsEditMode, setStarsEditMode] = useState(false);    // admin edit vs view
  const [localStarSelections, setLocalStarSelections] = useState({}); // { cacheDocId: [name, ...] }
  const [starsSaving, setStarsSaving] = useState(false);

  // Click stats state (admin only)
  const [clickStats, setClickStats] = useState({});
  const [clickPopup, setClickPopup] = useState({ 
    show: false, 
    buttonType: '', 
    competitionName: '', 
    stats: null 
  });

  // Fetch click stats for admin (including historical data from visitorLogs)
  useEffect(() => {
    const fetchClickStats = async () => {
      if (!isAdmin) return;
      // Wait until competitions are loaded
      if (upcomingCompetitions.length === 0 && pastCompetitions.length === 0 && loading) return;
      
      try {
        // Combine all competitions for mapping names to IDs
        const allCompetitions = [...upcomingCompetitions, ...pastCompetitions];
        const stats = await getAllClickStatsWithHistory(allCompetitions);
        setClickStats(stats);
      } catch (error) {
        console.error("Error fetching click stats:", error);
      }
    };
    
    fetchClickStats();
  }, [isAdmin, upcomingCompetitions, pastCompetitions, loading]);

  // Fetch announcement settings
  useEffect(() => {
    const fetchAnnouncement = async () => {
      try {
        const announcementRef = doc(db, "homepage_settings", "announcement");
        const announcementDoc = await getDoc(announcementRef);
        
        if (announcementDoc.exists()) {
          setAnnouncement(announcementDoc.data());
        }
      } catch (error) {
        console.error("Error fetching announcement:", error);
      }
    };
    
    fetchAnnouncement();
  }, []);

  // Fetch upcoming competitions and past competitions from Firestore
  useEffect(() => {
    const fetchCompetitions = async () => {
      try {
        setLoading(true);
        
        // Fetch all competitions and filter on client side
        const competitionsQuery = query(
          collection(db, "homepage_competitions"),
          orderBy("createdAt", "desc")
        );
        const snapshot = await getDocs(competitionsQuery);
        const allCompetitions = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        // Filter competitions by type on client side
        const upcomingData = allCompetitions.filter(comp => comp.type === "upcoming");
        const pastData = allCompetitions.filter(comp => comp.type === "past");
        
        // Sort by sortOrder (ascending), then by createdAt (descending) as fallback
        const sortCompetitions = (competitions) => {
          return competitions.sort((a, b) => {
            const aOrder = a.sortOrder || 0;
            const bOrder = b.sortOrder || 0;
            if (aOrder !== bOrder) {
              return aOrder - bOrder; // Lower numbers first
            }
            // If same order, sort by createdAt (newest first)
            return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
          });
        };
        
        setUpcomingCompetitions(sortCompetitions(upcomingData));
        setPastCompetitions(sortCompetitions(pastData));

        // Immediately preload profile pictures for all selected star dancers
        // so they appear instantly when the popup is opened
        const selectedStarNames = [];
        allCompetitions.forEach(comp => {
          if (comp.starSelections) {
            Object.values(comp.starSelections).forEach(arr => {
              if (Array.isArray(arr)) selectedStarNames.push(...arr);
            });
          }
        });
        preloadDancerThumbnails([...new Set(selectedStarNames)]);

      } catch (error) {
        console.error("Error fetching competitions:", error);
      } finally {
        setLoading(false);
      }
    };

    const fetchRules = async () => {
      try {
        // Fetch Solo rules
        const rulesSoloQuery = query(collection(db, "homepage_rules_solo"));
        const snapshotSolo = await getDocs(rulesSoloQuery);
        if (!snapshotSolo.empty) {
          const rulesDoc = snapshotSolo.docs[0];
          setRulesContentSolo(rulesDoc.data().content || "");
        }

        // Fetch Group rules
        const rulesGroupQuery = query(collection(db, "homepage_rules_group"));
        const snapshotGroup = await getDocs(rulesGroupQuery);
        if (!snapshotGroup.empty) {
          const rulesDoc = snapshotGroup.docs[0];
          setRulesContentGroup(rulesDoc.data().content || "");
        }
      } catch (error) {
        console.error("Error fetching rules:", error);
      }
    };

    fetchCompetitions();
    fetchRules();
  }, []);

  // Log a track play or download event to Firestore
  const logTrackEvent = async (eventType, track, competitionId, competitionName) => {
    try {
      const user = auth.currentUser;
      await addDoc(collection(db, 'trackPlayStats'), {
        trackName: track.displayName || track.name || track.fileName,
        trackUrl: track.url,
        competitionId: competitionId || '',
        competitionName: competitionName || '',
        eventType,       // 'play' | 'download'
        userId: user ? user.uid : 'anonymous',
        userEmail: user ? user.email : null,
        userName: user ? (user.displayName || user.email) : null,
        timestamp: new Date(),
      });
    } catch (e) {
      // non-blocking
    }
  };

  // Load track play/download stats for admin
  const loadTrackStats = async (competitionId) => {
    if (!isAdmin) return;
    try {
      const q = query(
        collection(db, 'trackPlayStats'),
        where('competitionId', '==', competitionId)
      );
      const snap = await getDocs(q);
      const stats = {};
      snap.forEach(d => {
        const data = d.data();
        if (!stats[data.trackUrl]) stats[data.trackUrl] = { plays: [], downloads: [] };
        const entry = { userId: data.userId, userEmail: data.userEmail, userName: data.userName, timestamp: data.timestamp };
        if (data.eventType === 'play') stats[data.trackUrl].plays.push(entry);
        else if (data.eventType === 'download') stats[data.trackUrl].downloads.push(entry);
      });
      setTrackStats(stats);
    } catch (e) {
      console.error('Error loading track stats:', e);
    }
  };

  // Stop playback and reset position (keep track visible in player)
  const stopTrack = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    setIsPlaying(false);
    setAudioCurrentTime(0);
  };

  const formatDate = (dateString) => {
    return dateString || "Date TBA";
  };

  const handleCancelledClick = (competition) => {
    if (competition.cancelled) {
      setCancellationPopup({ show: true, competition: competition });
      logInteraction('popup_open', { 
        popup: 'Cancellation Info', 
        competition: competition.name,
        reason: competition.cancellationReason
      });
    }
  };

  const openPlaylist = (e, competition) => {
    e.stopPropagation(); // Prevent triggering cancelled popup
    setPlaylistPopup({ show: true, competition: competition });
    setCurrentTrack(null);
    setIsPlaying(false);
    setActivePlaylistTab(null); // Reset tab, will be set to first category
    setTrackStats({});
    setExpandedStatsTrack(null);
    logInteraction('popup_open', { 
      popup: 'Playlist', 
      competition: competition.name,
      trackCount: competition.playlist?.length || 0
    });
    if (isAdmin) loadTrackStats(competition.id);
  };

  const openPrizeMoney = (e, competition) => {
    e.stopPropagation(); // Prevent triggering cancelled popup
    setPrizeMoneyPopup({ show: true, competition: competition });
    // Set first category as active tab
    const categories = Object.keys(competition.prizeMoney?.categories || {});
    setActivePrizeTab(categories[0] || null);
    logInteraction('popup_open', { 
      popup: 'Prize Money', 
      competition: competition.name,
      categories: categories
    });
  };

  const closePrizeMoney = () => {
    setPrizeMoneyPopup({ show: false, competition: null });
    setActivePrizeTab(null);
  };

  const closePlaylist = () => {
    if (audioRef.current) {
      audioRef.current.pause();
    }
    setPlaylistPopup({ show: false, competition: null });
    setCurrentTrack(null);
    setIsPlaying(false);
    setAudioCurrentTime(0);
    setAudioDuration(0);
    setTrackStats({});
    setExpandedStatsTrack(null);
  };

  const playTrack = (track) => {
    const competition = playlistPopup.competition;
    const competitionName = competition?.name;
    const competitionId = competition?.id;
    if (currentTrack?.url === track.url && isPlaying) {
      // Pause current track
      audioRef.current?.pause();
      setIsPlaying(false);
    } else {
      // Play new track or resume
      setCurrentTrack(track);
      setIsPlaying(true);
      setAudioCurrentTime(0);
      setAudioDuration(0);
      if (audioRef.current) {
        audioRef.current.src = track.url;
        audioRef.current.play().catch(err => console.error("Play error:", err));
      }
      logInteraction('audio_play', { 
        track: track.displayName || track.name,
        dance: track.dance || 'Unknown',
        category: track.category || 'Unknown',
        competition: competitionName
      });
      logTrackEvent('play', track, competitionId, competitionName);
    }
  };

  const handleAudioEnded = () => {
    setIsPlaying(false);
    setAudioCurrentTime(0);
  };

  // Download track directly to device
  const downloadTrack = async (e, track) => {
    e.preventDefault();
    e.stopPropagation();
    logTrackEvent('download', track, playlistPopup.competition?.id, playlistPopup.competition?.name);
    try {
      const response = await fetch(track.url);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = track.fileName || track.displayName || track.name || 'track.mp3';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Download error:", error);
      // Fallback: open in new tab
      window.open(track.url, '_blank');
    }
  };

  // Toggle category collapse/expand in playlist
  const toggleCategoryCollapse = (categoryKey) => {
    // Not used anymore with tabs, but keep for potential future use
  };

  // Group playlist tracks by age category (for tabs) and then by round
  const groupPlaylistByAgeAndRound = (playlist) => {
    if (!playlist || playlist.length === 0) return null;
    
    const grouped = {};
    let hasCategories = false;
    
    playlist.forEach(track => {
      const ageCategory = track.category || "Uncategorized";
      const round = track.round || "General";
      
      if (track.category) hasCategories = true;
      
      if (!grouped[ageCategory]) {
        grouped[ageCategory] = {};
      }
      if (!grouped[ageCategory][round]) {
        grouped[ageCategory][round] = [];
      }
      grouped[ageCategory][round].push(track);
    });
    
    // If no tracks have categories, return null to show flat list
    if (!hasCategories) return null;
    
    return grouped;
  };

  // Get ordered round names for display
  const getRoundOrder = (rounds) => {
    const roundOrder = ["Final", "Semi Final", "Quarter Final", "Preliminary", "General"];
    return Object.keys(rounds).sort((a, b) => {
      const aIndex = roundOrder.indexOf(a);
      const bIndex = roundOrder.indexOf(b);
      if (aIndex === -1 && bIndex === -1) return a.localeCompare(b);
      if (aIndex === -1) return 1;
      if (bIndex === -1) return -1;
      return aIndex - bIndex;
    });
  };

  // Handle button click with tracking
  const handleButtonClick = async (competition, buttonType, url) => {
    // Track the click (for all users, stored permanently)
    await trackButtonClick(competition.id, competition.name, buttonType, url);
    
    // Also log interaction for visitor tracking
    logInteraction('link_click', { 
      button: buttonType, 
      competition: competition.name,
      url: url 
    });
    
    // Refresh click stats for admin
    if (isAdmin) {
      const allCompetitions = [...upcomingCompetitions, ...pastCompetitions];
      const stats = await getAllClickStatsWithHistory(allCompetitions);
      setClickStats(stats);
    }
  };

  // Get click count for a specific button
  const getClickCount = (competitionId, buttonType) => {
    if (!clickStats[competitionId]) return 0;
    if (!clickStats[competitionId][buttonType]) return 0;
    return clickStats[competitionId][buttonType].totalClicks || 0;
  };

  // Show click details popup
  const showClickDetails = (e, competitionId, competitionName, buttonType) => {
    e.preventDefault();
    e.stopPropagation();
    
    const stats = clickStats[competitionId]?.[buttonType] || null;
    setClickPopup({
      show: true,
      buttonType,
      competitionName,
      stats
    });
  };

  // Close click details popup
  const closeClickPopup = () => {
    setClickPopup({ show: false, buttonType: '', competitionName: '', stats: null });
  };

  // Extract unique discipline names from competition categories array
  // e.g. ["Latin - Solo", "Contemporary - Group"] → ["Latin", "Contemporary"]
  const extractDisciplines = (categories) => {
    if (!categories || categories.length === 0) return [];
    const disciplines = new Set();
    categories.forEach(cat => {
      const match = cat.match(/^([^-]+)/);
      if (match) disciplines.add(match[1].trim());
    });
    return [...disciplines];
  };

  // Open the Stars popup and lazily load globalRankingsCache once
  const openStarsPopup = async (e, competition) => {
    e.stopPropagation();
    trackButtonClick(competition.id, competition.name, 'Stars').catch(() => {});
    logInteraction('link_click', {
      button: 'Stars',
      competition: competition.name,
    });
    // Refresh click stats so admin badge updates immediately
    if (isAdmin) {
      const allCompetitions = [...upcomingCompetitions, ...pastCompetitions];
      getAllClickStatsWithHistory(allCompetitions).then(stats => setClickStats(stats)).catch(() => {});
    }
    setStarsEditMode(false);
    setStarsActiveTab(null);
    setStarsInnerTab(null);
    setLocalStarSelections(competition.starSelections || {});
    setStarsPopup({ show: true, competition });
    if (!globalStarsCache) {
      setStarsLoading(true);
      try {
        const snap = await getDocs(collection(db, "globalRankingsCache"));
        const cats = [];
        // Helper to get a JS Date from a Firestore Timestamp or Date field
        const toDate = (v) => {
          if (!v) return new Date(0);
          if (typeof v.toDate === 'function') return v.toDate();
          if (v.seconds) return new Date(v.seconds * 1000);
          return new Date(v);
        };
        snap.forEach(d => {
          if (d.id === "_metadata") return;
          const data = d.data();
          if (!data.rankings || data.rankings.length === 0) return;
          const top3 = data.rankings
            .filter(r => r.currentRank <= 3)
            .sort((a, b) => a.currentRank - b.currentRank)
            .slice(0, 3)
            .map(r => {
              // Use the most recent competition result to get the current studio
              const sortedByDate = [...(r.results || [])].sort(
                (a, b) => toDate(b.resultDate || b.timestamp) - toDate(a.resultDate || a.timestamp)
              );
              return { ...r, currentStudio: sortedByDate[0]?.studio || null };
            });
          if (top3.length === 0) return;
          cats.push({
            id: d.id,
            discipline: data.discipline || "Unknown",
            category:   data.category   || d.id,
            dance:      data.dance      || null,
            top3,
          });
        });
        setGlobalStarsCache(cats);
        // Preload pictures only for this competition's selected dancers
        const selNames = Object.values(competition.starSelections || {}).flat().filter(Boolean);
        preloadDancerThumbnails([...new Set(selNames)]);
      } catch (err) {
        console.error("Error loading star rankings:", err);
      } finally {
        setStarsLoading(false);
      }
    } else {
      // Cache already loaded — preload selected dancers for this competition
      const selNames = Object.values(competition.starSelections || {}).flat().filter(Boolean);
      preloadDancerThumbnails([...new Set(selNames)]);
    }
  };

  // Save admin star selections back to the homepage_competitions document
  const saveStarSelections = async () => {
    if (!starsPopup.competition) return;
    setStarsSaving(true);
    try {
      const compRef = doc(db, "homepage_competitions", starsPopup.competition.id);
      await updateDoc(compRef, { starSelections: localStarSelections });
      // Update in-memory competition object so UI reflects immediately
      setStarsPopup(prev => ({
        ...prev,
        competition: { ...prev.competition, starSelections: localStarSelections }
      }));
      setStarsEditMode(false);
    } catch (err) {
      console.error("Error saving star selections:", err);
      alert("Failed to save. Please try again.");
    } finally {
      setStarsSaving(false);
    }
  };

  // Toggle a dancer's name in/out of the local selection for a category
  const toggleStarSelection = (catId, dancerName) => {
    setLocalStarSelections(prev => {
      const current = prev[catId] || [];
      const exists = current.includes(dancerName);
      const updated = exists
        ? current.filter(n => n !== dancerName)
        : [...current, dancerName];
      return { ...prev, [catId]: updated };
    });
  };

  const renderCompetitionCard = (competition) => (
    <div 
      key={competition.id} 
      className={`competition-card-modern ${competition.cancelled ? 'competition-card-cancelled' : ''}`}
      onClick={() => handleCancelledClick(competition)}
      style={competition.cancelled ? { cursor: 'pointer' } : {}}
    >
      {/* Cancelled overlay */}
      {competition.cancelled && (
        <div className="cancelled-overlay">
          <span className="cancelled-text">CANCELLED</span>
        </div>
      )}
      {/* Main Content Section: Logo, Info, and Categories */}
      <div className="competition-main-content">
        <div className="competition-logo-section">
          {competition.logoUrl ? (
            <img 
              src={competition.logoUrl} 
              alt={`${competition.name} logo`}
              className="competition-logo-modern"
            />
          ) : (
            <div className="competition-logo-placeholder">
              🏆
            </div>
          )}
        </div>
        
        <div className="competition-info-section">
          <h3 className="competition-title">{competition.name}</h3>
          <p className="competition-date-modern">📅 {formatDate(competition.date)}</p>
          {competition.location && (
            <p className="competition-location-modern">📍 {competition.location}</p>
          )}
          
          {/* Competition Categories - Moved here and made smaller */}
          {competition.categories && competition.categories.length > 0 && (
            <div className="competition-categories-inline">
              <div className="categories-inline-grid">
                {competition.categories.map((category, index) => (
                  <span key={index} className="category-badge-inline">
                    {category}
                  </span>
                ))}
              </div>
            </div>
          )}
          
          {/* Competition Links */}
          {(showPredictFor(competition) || competition.website || competition.registration || competition.schedule || competition.results || (competition.playlist && competition.playlist.length > 0) || competition.prizeMoney || (competition.categories && competition.categories.some(c => /solo/i.test(c)))) && (
            <div className="competition-links-modern">
              {competition.website && (
                <span className="button-with-stats">
                  <a 
                    href={competition.website} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="competition-link-modern website-link"
                    onClick={() => handleButtonClick(competition, 'Website', competition.website)}
                  >
                    🌐 Website
                  </a>
                  {isAdmin && getClickCount(competition.id, 'Website') > 0 && (
                    <span 
                      className="click-count-badge"
                      onClick={(e) => showClickDetails(e, competition.id, competition.name, 'Website')}
                      title="Click to see details"
                    >
                      ({getClickCount(competition.id, 'Website')})
                    </span>
                  )}
                </span>
              )}
              {competition.registration && competition.type !== "past" && (
                <span className="button-with-stats">
                  <a 
                    href={competition.registration} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="competition-link-modern registration-link"
                    onClick={() => handleButtonClick(competition, 'Registration', competition.registration)}
                  >
                    📝 Registration
                  </a>
                  {isAdmin && getClickCount(competition.id, 'Registration') > 0 && (
                    <span 
                      className="click-count-badge"
                      onClick={(e) => showClickDetails(e, competition.id, competition.name, 'Registration')}
                      title="Click to see details"
                    >
                      ({getClickCount(competition.id, 'Registration')})
                    </span>
                  )}
                </span>
              )}
              {competition.schedule && (
                <span className="button-with-stats">
                  <a 
                    href={competition.schedule} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className={`competition-link-modern schedule-link ${competition.scheduleUpdated ? 'schedule-updated-blink' : ''}`}
                    onClick={() => handleButtonClick(competition, 'Schedule', competition.schedule)}
                  >
                    {competition.scheduleUpdated ? (
                      <span className="schedule-blink-text">
                        <span className="blink-word-1">📋 Updated</span>
                        <span className="blink-word-2">📋 Schedule</span>
                      </span>
                    ) : (
                      "📋 Schedule"
                    )}
                  </a>
                  {isAdmin && getClickCount(competition.id, 'Schedule') > 0 && (
                    <span 
                      className="click-count-badge"
                      onClick={(e) => showClickDetails(e, competition.id, competition.name, 'Schedule')}
                      title="Click to see details"
                    >
                      ({getClickCount(competition.id, 'Schedule')})
                    </span>
                  )}
                </span>
              )}
              {competition.results && (
                <span className="button-with-stats">
                  <a 
                    href={competition.results} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="competition-link-modern results-link"
                    onClick={() => handleButtonClick(competition, 'Results', competition.results)}
                  >
                    🏆 Results
                  </a>
                  {isAdmin && getClickCount(competition.id, 'Results') > 0 && (
                    <span 
                      className="click-count-badge"
                      onClick={(e) => showClickDetails(e, competition.id, competition.name, 'Results')}
                      title="Click to see details"
                    >
                      ({getClickCount(competition.id, 'Results')})
                    </span>
                  )}
                </span>
              )}
              {competition.prizeMoney && Object.keys(competition.prizeMoney.categories || {}).length > 0 && (
                <span className="button-with-stats">
                  <button
                    onClick={(e) => {
                      handleButtonClick(competition, 'PrizeMoney', null);
                      openPrizeMoney(e, competition);
                    }}
                    className="competition-link-modern prize-money-link"
                  >
                    💰 Prize Money
                  </button>
                  {isAdmin && getClickCount(competition.id, 'PrizeMoney') > 0 && (
                    <span 
                      className="click-count-badge"
                      onClick={(e) => showClickDetails(e, competition.id, competition.name, 'PrizeMoney')}
                      title="Click to see details"
                    >
                      ({getClickCount(competition.id, 'PrizeMoney')})
                    </span>
                  )}
                </span>
              )}
              {competition.playlist && competition.playlist.length > 0 && (
                <span className="button-with-stats">
                  <button
                    onClick={(e) => {
                      handleButtonClick(competition, 'Playlist', null);
                      openPlaylist(e, competition);
                    }}
                    className="competition-link-modern playlist-link"
                  >
                    🎵 Playlist ({competition.playlist.length})
                  </button>
                  {isAdmin && getClickCount(competition.id, 'Playlist') > 0 && (
                    <span 
                      className="click-count-badge"
                      onClick={(e) => showClickDetails(e, competition.id, competition.name, 'Playlist')}
                      title="Click to see details"
                    >
                      ({getClickCount(competition.id, 'Playlist')})
                    </span>
                  )}
                </span>
              )}
              {competition.categories && competition.categories.some(c => /solo/i.test(c)) &&
                (isAdmin || Object.values(competition.starSelections || {}).some(arr => Array.isArray(arr) && arr.length > 0)) && (
                <span className="button-with-stats">
                  <button
                    onClick={(e) => openStarsPopup(e, competition)}
                    className="competition-link-modern stars-link"
                  >
                    ⭐ Stars
                  </button>
                  {isAdmin && getClickCount(competition.id, 'Stars') > 0 && (
                    <span
                      className="click-count-badge"
                      onClick={(e) => showClickDetails(e, competition.id, competition.name, 'Stars')}
                      title="Click to see details"
                    >
                      ({getClickCount(competition.id, 'Stars')})
                    </span>
                  )}
                </span>
              )}
              {showPredictFor(competition) && (
                <span className="button-with-stats">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleButtonClick(competition, 'Predict', null);
                      setPredictModal(competition);
                    }}
                    className="competition-link-modern predict-link"
                  >
                    {predictLabel(competition)}
                  </button>
                </span>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  // Get text size style based on setting
  const getAnnouncementTextSize = () => {
    const sizes = {
      small: { desktop: '0.85rem', mobile: '0.8rem' },
      medium: { desktop: '1rem', mobile: '0.9rem' },
      large: { desktop: '1.15rem', mobile: '1.05rem' }
    };
    const size = announcement.textSize || 'medium';
    const isMobile = window.innerWidth <= 768;
    return sizes[size]?.[isMobile ? 'mobile' : 'desktop'] || sizes.medium.desktop;
  };

  return (
    <div className="container homepage">
      {/* Important Announcement Banner - Only show if enabled and has text */}
      {announcement.enabled && announcement.text && (
        <div className={`important-announcement-banner ${announcement.icon === "none" ? "no-icon" : ""}`}>
          {announcement.icon && announcement.icon !== "none" && (
            <div className="announcement-icon">{announcement.icon}</div>
          )}
          <div
            className="announcement-text"
            style={{ fontSize: getAnnouncementTextSize() }}
            dangerouslySetInnerHTML={{
              __html: announcement.text.includes('<')
                ? announcement.text
                : announcement.text.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
            }}
          />
        </div>
      )}

      {/* 🔥 Rank movement widgets */}
      <RankMovementWidgets />

      {/* Tab Navigation */}
      <div className="tab-navigation">
        <button 
          className={`tab-button ${activeTab === "upcoming" ? "active" : ""}`}
          onClick={() => setActiveTab("upcoming")}
        >
          <span className="desktop-tab-text">📅 Upcoming Competitions</span>
          <span className="mobile-tab-text">📅 Upcoming</span>
        </button>
        <button 
          className={`tab-button ${activeTab === "past" ? "active" : ""}`}
          onClick={() => setActiveTab("past")}
        >
          <span className="desktop-tab-text">🏆 Past Competitions</span>
          <span className="mobile-tab-text">🏆 Past</span>
        </button>
        <button 
          className={`tab-button ${activeTab === "rules" ? "active" : ""}`}
          onClick={() => setActiveTab("rules")}
        >
          <span className="desktop-tab-text">📋 Rules & Regulations</span>
          <span className="mobile-tab-text">📋 Rules</span>
        </button>
      </div>

      {/* Free Latin Music Blinking Message */}
      <div 
        className="free-music-banner"
        onClick={async () => {
          // Track banner click
          await trackButtonClick('music_banner', 'Free Latin Music', 'banner_click', 'https://t.me/dj_starseed');
          logInteraction('music_banner_click', { 
            action: 'open_popup',
            destination: 'https://t.me/dj_starseed'
          });
          
          setShowMusicPopup(true);
          
          // Refresh stats for admin
          if (isAdmin) {
            const allCompetitions = [...upcomingCompetitions, ...pastCompetitions];
            const stats = await getAllClickStatsWithHistory(allCompetitions);
            setClickStats(stats);
          }
        }}
      >
        <span className="blinking-text">🎵 Free Latin Music 🎵</span>
        {isAdmin && clickStats['music_banner']?.['banner_click'] && (
          <span 
            className="music-click-badge"
            onClick={(e) => showClickDetails(e, 'music_banner', 'Free Latin Music', 'banner_click')}
            title="Click to see details"
          >
            👁️ {clickStats['music_banner']['banner_click'].totalClicks || 0}
          </span>
        )}
      </div>

      {/* Tab Content */}
      <div className="tab-content">
        {activeTab === "upcoming" && (
          <div className="tab-panel">
            <h2>📅 Upcoming Competitions</h2>
            {loading ? (
              <p>Loading upcoming competitions...</p>
            ) : upcomingCompetitions.length > 0 ? (
              <div className="competitions-list">
                {upcomingCompetitions.map(renderCompetitionCard)}
              </div>
            ) : (
              <p>No upcoming competitions at the moment.</p>
            )}
          </div>
        )}

        {activeTab === "past" && (
          <div className="tab-panel">
            <h2>🏆 Past Competitions</h2>
            {loading ? (
              <p>Loading past competitions...</p>
            ) : pastCompetitions.length > 0 ? (
              <div className="competitions-list">
                {pastCompetitions.map(renderCompetitionCard)}
              </div>
            ) : (
              <p>No past competitions at the moment.</p>
            )}
          </div>
        )}

        {activeTab === "rules" && (
          <div className="tab-panel">
            <h2>📋 Rules & Regulations</h2>
            
            {/* Sub-tabs for Solo and Group */}
            <div className="rules-sub-tabs">
              <button
                className={`sub-tab-button ${rulesSubTab === "solo" ? "active" : ""}`}
                onClick={() => setRulesSubTab("solo")}
              >
                🕺 Solo
              </button>
              <button
                className={`sub-tab-button ${rulesSubTab === "group" ? "active" : ""}`}
                onClick={() => setRulesSubTab("group")}
              >
                👥 Group
              </button>
            </div>

            {/* Solo Rules Content */}
            {rulesSubTab === "solo" && (
              <div className="rules-content">
                {rulesContentSolo ? (
                  <div 
                    dangerouslySetInnerHTML={{ __html: rulesContentSolo }}
                    style={{ lineHeight: '1.6' }}
                  />
                ) : (
                  <div>
                    <h3>Solo Competition Rules</h3>
                    <ul>
                      <li>All participants must register before the competition deadline</li>
                      <li>Age categories are determined by the participant's age on January 1st of the competition year</li>
                      <li>Participants can compete in multiple disciplines</li>
                    </ul>

                    <h3>Disciplines</h3>
                    <ul>
                      <li><strong>Latin:</strong> Includes Cha-cha, Samba, Rumba, Paso Doble, and Jive</li>
                      <li><strong>Contemporary:</strong> Modern interpretive dance styles</li>
                      <li><strong>Hip Hop:</strong> Street dance and urban choreography</li>
                      <li><strong>Commercial:</strong> Industry-style dance for music videos and performances (from 2026)</li>
                    </ul>

                    <h3>Scoring System</h3>
                    <p>Solo competitions are judged based on technical skill, artistic expression, and overall performance quality.</p>
                  </div>
                )}
              </div>
            )}

            {/* Group Rules Content */}
            {rulesSubTab === "group" && (
              <div className="rules-content">
                {rulesContentGroup ? (
                  <div 
                    dangerouslySetInnerHTML={{ __html: rulesContentGroup }}
                    style={{ lineHeight: '1.6' }}
                  />
                ) : (
                  <div>
                    <h3>Group Competition Rules</h3>
                    <ul>
                      <li>Groups must consist of 3 or more dancers</li>
                      <li>All group members must register before the competition deadline</li>
                      <li>Age categories are determined by the average age of group members</li>
                    </ul>

                    <h3>Disciplines</h3>
                    <ul>
                      <li><strong>Latin:</strong> Team performances in Latin dance styles</li>
                      <li><strong>Contemporary:</strong> Modern group choreography</li>
                      <li><strong>Hip Hop:</strong> Street dance group routines</li>
                      <li><strong>Commercial:</strong> Industry-style group performances (from 2026)</li>
                    </ul>

                    <h3>Scoring System</h3>
                    <p>Group competitions are judged based on synchronization, technical skill, creativity, and overall performance quality.</p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Cancellation Popup Modal */}
      {cancellationPopup.show && cancellationPopup.competition && (
        <div 
          className="cancellation-modal-overlay"
          onClick={() => setCancellationPopup({ show: false, competition: null })}
        >
          <div 
            className="cancellation-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="cancellation-modal-header">
              <h3>❌ Competition Cancelled</h3>
              <button 
                className="cancellation-modal-close"
                onClick={() => setCancellationPopup({ show: false, competition: null })}
              >
                ✕
              </button>
            </div>
            <div className="cancellation-modal-body">
              <p className="cancelled-competition-name">{cancellationPopup.competition.name}</p>
              <p className="cancellation-reason-label">Reason:</p>
              <p className="cancellation-reason-text">
                {cancellationPopup.competition.cancellationReason || "No reason provided"}
              </p>
            </div>
            <div className="cancellation-modal-footer">
              <button 
                className="cancellation-modal-btn"
                onClick={() => setCancellationPopup({ show: false, competition: null })}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Playlist Popup Modal */}
      {playlistPopup.show && playlistPopup.competition && (
        <div 
          className="playlist-modal-overlay"
          onClick={closePlaylist}
        >
          <div 
            className="playlist-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="playlist-modal-header">
              <h3>🎵 Competition Playlist</h3>
              <button 
                className="playlist-modal-close"
                onClick={closePlaylist}
              >
                ✕
              </button>
            </div>
            <div className="playlist-modal-subheader">
              <p>{playlistPopup.competition.name}</p>
            </div>
            <div className="playlist-modal-body">
              {/* Hidden audio element */}
              <audio 
                ref={audioRef} 
                onEnded={handleAudioEnded}
                onTimeUpdate={() => setAudioCurrentTime(audioRef.current?.currentTime || 0)}
                onLoadedMetadata={() => setAudioDuration(audioRef.current?.duration || 0)}
                style={{ display: 'none' }}
              />

              {/* Helper to render a single track row */}
              {(() => {
                // Shared track row renderer used by both tabbed and flat view
                const renderTrackRow = (track, index) => {
                  const isThisPlaying = currentTrack?.url === track.url && isPlaying;
                  const isThisActive  = currentTrack?.url === track.url;
                  const stats = trackStats[track.url];
                  const hasStats = isAdmin && stats && (stats.plays?.length > 0 || stats.downloads?.length > 0);
                  const statsOpen = expandedStatsTrack === track.url;

                  return (
                    <div
                      key={index}
                      className={`playlist-track${isThisActive ? ' playing' : ''}`}
                    >
                      <div className="track-info">
                        <span className="track-number">{index + 1}.</span>
                        {track.dance && <span className="track-dance">{track.dance}</span>}
                        <span className="track-name">{track.displayName || track.name || track.fileName}</span>
                        {isAdmin && (
                          <div className="track-stat-badges">
                            {(stats?.plays?.length > 0) && (
                              <span
                                className="track-stat-badge plays"
                                onClick={(e) => { e.stopPropagation(); setExpandedStatsTrack(statsOpen ? null : track.url); }}
                                title="Plays — click to expand"
                              >▶ {stats.plays.length}</span>
                            )}
                            {(stats?.downloads?.length > 0) && (
                              <span
                                className="track-stat-badge downloads"
                                onClick={(e) => { e.stopPropagation(); setExpandedStatsTrack(statsOpen ? null : track.url); }}
                                title="Downloads — click to expand"
                              >⬇ {stats.downloads.length}</span>
                            )}
                          </div>
                        )}
                      </div>
                      <div className="track-actions">
                        <button
                          onClick={() => playTrack(track)}
                          className="track-play-btn"
                          title={isThisPlaying ? "Pause" : "Play"}
                        >
                          {isThisPlaying ? "⏸️" : "▶️"}
                        </button>
                        <button
                          onClick={(e) => downloadTrack(e, track)}
                          className="track-download-btn"
                          title="Download"
                        >
                          ⬇️
                        </button>
                      </div>
                      {/* Stats expansion panel (admin only) */}
                      {statsOpen && (
                        <div className="track-stats-panel">
                          {stats?.plays?.length > 0 && (
                            <div className="track-stats-section">
                              <h4>▶ Plays ({stats.plays.length})</h4>
                              <div className="track-stats-user-list">
                                {[...stats.plays].reverse().map((p, i) => (
                                  <div key={i} className="track-stats-user-item">
                                    <span className="u-name">{p.userName || p.userEmail || 'Anonymous'}</span>
                                    <span className="u-time">{p.timestamp?.toDate ? p.timestamp.toDate().toLocaleString('el-GR') : new Date(p.timestamp).toLocaleString('el-GR')}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          {stats?.downloads?.length > 0 && (
                            <div className="track-stats-section">
                              <h4>⬇ Downloads ({stats.downloads.length})</h4>
                              <div className="track-stats-user-list">
                                {[...stats.downloads].reverse().map((p, i) => (
                                  <div key={i} className="track-stats-user-item">
                                    <span className="u-name">{p.userName || p.userEmail || 'Anonymous'}</span>
                                    <span className="u-time">{p.timestamp?.toDate ? p.timestamp.toDate().toLocaleString('el-GR') : new Date(p.timestamp).toLocaleString('el-GR')}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                };

                const groupedByAge = groupPlaylistByAgeAndRound(playlistPopup.competition.playlist);

                // Tabbed view (tracks with age categories)
                if (groupedByAge) {
                  const ageCategories = Object.keys(groupedByAge);
                  const currentTab = activePlaylistTab || ageCategories[0];
                  const currentRounds = groupedByAge[currentTab] || {};
                  const orderedRounds = getRoundOrder(currentRounds);
                  return (
                    <div className="playlist-tabbed-view">
                      <div className="playlist-tabs">
                        {ageCategories.map(category => (
                          <button
                            key={category}
                            className={`playlist-tab ${(activePlaylistTab || ageCategories[0]) === category ? 'active' : ''}`}
                            onClick={() => setActivePlaylistTab(category)}
                          >
                            {category}
                          </button>
                        ))}
                      </div>
                      <div className="playlist-tab-content">
                        {orderedRounds.map(round => (
                          <div key={round} className="playlist-round-group">
                            <div className="playlist-round-header">
                              <span className="round-icon">🏆</span>
                              <span className="round-name">{round}</span>
                              <span className="round-count">({currentRounds[round].length} tracks)</span>
                            </div>
                            <div className="playlist-tracks">
                              {currentRounds[round].map((track, index) => renderTrackRow(track, index))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                }

                // Flat list
                return (
                  <div className="playlist-tracks">
                    {playlistPopup.competition.playlist.map((track, index) => renderTrackRow(track, index))}
                  </div>
                );
              })()}
            </div>

            {/* ── Player bar — always visible outside the scroll area ── */}
            <div className="playlist-player">
              {currentTrack ? (
                <>
                  <div className="playlist-player-track">
                    {currentTrack.dance && <span className="playlist-player-track-dance">{currentTrack.dance} ·</span>}
                    <span className="playlist-player-track-name">{currentTrack.displayName || currentTrack.name || currentTrack.fileName}</span>
                  </div>
                  <div className="playlist-player-row">
                    <div className="player-controls">
                      <button
                        className="player-btn player-btn-play"
                        title={isPlaying ? "Pause" : "Play"}
                        onClick={() => {
                          if (isPlaying) {
                            audioRef.current?.pause();
                            setIsPlaying(false);
                          } else {
                            audioRef.current?.play().catch(() => {});
                            setIsPlaying(true);
                          }
                        }}
                      >
                        {isPlaying ? "⏸" : "▶"}
                      </button>
                      <button
                        className="player-btn player-btn-stop"
                        title="Stop"
                        onClick={stopTrack}
                      >
                        ⏹
                      </button>
                    </div>
                    <div className="player-progress">
                      <span className="player-time">{formatAudioTime(audioCurrentTime)}</span>
                      <input
                        type="range"
                        min={0}
                        max={audioDuration || 0}
                        value={audioCurrentTime}
                        step={0.5}
                        className="player-seek"
                        onChange={(e) => {
                          const t = parseFloat(e.target.value);
                          if (audioRef.current) audioRef.current.currentTime = t;
                          setAudioCurrentTime(t);
                        }}
                      />
                      <span className="player-time">-{formatAudioTime(Math.max(0, (audioDuration || 0) - audioCurrentTime))}</span>
                    </div>
                  </div>
                </>
              ) : (
                <div className="playlist-player-empty">▶ Select a track to play</div>
              )}
            </div>

            <div className="playlist-modal-footer">
              <a 
                href="https://t.me/dj_starseed" 
                target="_blank" 
                rel="noopener noreferrer"
                className="playlist-music-credit"
              >
                🎵 Free Latin Music - DJ Starseed
              </a>
              <button 
                className="playlist-modal-btn"
                onClick={closePlaylist}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Prize Money Popup Modal */}
      {prizeMoneyPopup.show && prizeMoneyPopup.competition && prizeMoneyPopup.competition.prizeMoney && (
        <div 
          className="prize-money-modal-overlay"
          onClick={closePrizeMoney}
        >
          <div 
            className="prize-money-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="prize-money-modal-header">
              <h3>💰 Prize Money</h3>
              <button 
                className="prize-money-modal-close"
                onClick={closePrizeMoney}
              >
                ✕
              </button>
            </div>
            <div className="prize-money-modal-subheader">
              <p>{prizeMoneyPopup.competition.name}</p>
              {prizeMoneyPopup.competition.prizeMoney.title && (
                <p className="prize-money-title">{prizeMoneyPopup.competition.prizeMoney.title}</p>
              )}
            </div>
            <div className="prize-money-modal-body">
              {/* Category Tabs */}
              {(() => {
                const categories = Object.keys(prizeMoneyPopup.competition.prizeMoney.categories || {});
                const currentTab = activePrizeTab || categories[0];
                const currentCategory = prizeMoneyPopup.competition.prizeMoney.categories[currentTab];

                return (
                  <div className="prize-money-tabbed-view">
                    {/* Category Tabs */}
                    {categories.length > 1 && (
                      <div className="prize-money-tabs">
                        {categories.map(category => (
                          <button
                            key={category}
                            className={`prize-money-tab ${(activePrizeTab || categories[0]) === category ? 'active' : ''}`}
                            onClick={() => setActivePrizeTab(category)}
                          >
                            {category}
                          </button>
                        ))}
                      </div>
                    )}

                    {/* Single category header if only one */}
                    {categories.length === 1 && (
                      <div className="prize-money-single-category">
                        <h4>🏆 {categories[0]}</h4>
                      </div>
                    )}

                    {/* Prize List for Selected Tab */}
                    <div className="prize-money-tab-content">
                      {categories.length > 1 && (
                        <div className="prize-money-category-header">
                          <span className="prize-category-icon">🏆</span>
                          <span className="prize-category-name">{currentTab}</span>
                        </div>
                      )}
                      <div className="prize-money-list">
                        {currentCategory?.prizes?.map((prize, index) => (
                          <div key={index} className="prize-money-item">
                            <div className="prize-place">
                              <span className="place-medal">
                                {prize.place === "1st" || prize.place?.toLowerCase().includes("1") ? "🥇" : 
                                 prize.place === "2nd" || prize.place?.toLowerCase().includes("2") ? "🥈" : 
                                 prize.place === "3rd" || prize.place?.toLowerCase().includes("3") ? "🥉" : "🏅"}
                              </span>
                              <span className="place-text">{prize.place}</span>
                            </div>
                            <div className="prize-amount">
                              <span className="amount-value">{prize.amount ? Number(prize.amount).toLocaleString() : "0"}</span>
                              <span className="amount-currency">€</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>
            <div className="prize-money-modal-footer">
              <button 
                className="prize-money-modal-btn"
                onClick={closePrizeMoney}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Click Details Popup (Admin only) */}
      <ClickDetailsPopup 
        isOpen={clickPopup.show}
        onClose={closeClickPopup}
        buttonType={clickPopup.buttonType}
        competitionName={clickPopup.competitionName}
        stats={clickPopup.stats}
      />

      {/* Stars Popup Modal */}
      {starsPopup.show && starsPopup.competition && createPortal(
        <div
          className="stars-modal-overlay"
          onClick={() => setStarsPopup({ show: false, competition: null })}
        >
          <div className="stars-modal" onClick={(e) => e.stopPropagation()}>

            {/* Header */}
            <div className="stars-modal-header">
              <div>
                <h3 className="stars-modal-title">⭐ Stars</h3>
                <p className="stars-modal-subtitle">Dancers entering from Round 2</p>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                {isAdmin && !starsLoading && (
                  <button
                    className={`stars-mode-toggle ${starsEditMode ? "active" : ""}`}
                    onClick={() => {
                      if (starsEditMode) setLocalStarSelections(starsPopup.competition.starSelections || {});
                      setStarsEditMode(m => !m);
                    }}
                  >
                    {starsEditMode ? "✕ Cancel" : "✏️ Edit"}
                  </button>
                )}
                <button
                  className="stars-modal-close"
                  onClick={() => setStarsPopup({ show: false, competition: null })}
                >✕</button>
              </div>
            </div>

            {/* Competition name */}
            <div className="stars-modal-comp-name">{starsPopup.competition.name}</div>

            {/* Body */}
            <div className="stars-modal-body">
              {starsLoading ? (
                <div className="stars-modal-loading">
                  <div className="stars-modal-spinner" />
                  <p>Loading star rankings…</p>
                </div>
              ) : (() => {
                const disciplines = extractDisciplines(starsPopup.competition.categories);
                const allCats = globalStarsCache || [];
                const filtered = disciplines.length > 0
                  ? allCats.filter(c => disciplines.includes(c.discipline))
                  : allCats;

                if (filtered.length === 0) {
                  return (
                    <div className="stars-modal-empty">
                      <p>No star rankings found for this competition's disciplines.</p>
                      <a href="/ranking" className="stars-modal-view-link" target="_blank" rel="noreferrer">
                        View all rankings →
                      </a>
                    </div>
                  );
                }

                const getAge = (catStr) => {
                  for (const age of STAR_AGE_ORDER) {
                    if (catStr.includes(age)) return age;
                  }
                  return "Other";
                };

                const latinCats = filtered.filter(c => c.discipline === "Latin");
                const latinAges = STAR_AGE_ORDER.filter(age =>
                  latinCats.some(c => getAge(c.category) === age)
                );

                // Build sidebar tabs:
                //   Latin → one tab per age category  (id = "Latin|Adult", "Latin|Youth", …)
                //   Other → one tab per discipline     (id = "Contemporary", "Hip Hop", …)
                const sidebarTabs = [];
                if (latinCats.length > 0) {
                  latinAges.forEach(age =>
                    sidebarTabs.push({ id: `Latin|${age}`, label: age, disc: "Latin" })
                  );
                }
                STAR_DISCIPLINE_ORDER
                  .filter(d => d !== "Latin" && filtered.some(c => c.discipline === d))
                  .forEach(disc => sidebarTabs.push({ id: disc, label: disc, disc }));

                const activeSidebarId    = starsActiveTab || sidebarTabs[0]?.id;
                const activeSidebarTab   = sidebarTabs.find(t => t.id === activeSidebarId) || sidebarTabs[0];
                const isLatinTab         = activeSidebarTab?.disc === "Latin";
                const color              = STAR_DISCIPLINE_COLORS[activeSidebarTab?.disc] || { bg: "#f5f5f5", border: "#999" };

                // Build inner tabs + find the active cat
                let innerTabs    = [];
                let activeCat    = null;
                let activeInnerKey = starsInnerTab;

                // Resolve selections source early — needed for auto-tab selection below
                const sel = starsEditMode
                  ? localStarSelections
                  : (starsPopup.competition.starSelections || {});

                if (isLatinTab) {
                  const selectedAge = activeSidebarTab.id.split("|")[1];
                  innerTabs = STAR_LATIN_DANCE_ORDER.filter(dance =>
                    latinCats.some(c => getAge(c.category) === selectedAge && c.dance === dance)
                  );
                  if (!activeInnerKey || !innerTabs.includes(activeInnerKey)) {
                    // Jump to first dance tab that has at least one star selected
                    const firstWithStars = innerTabs.find(dance => {
                      const cat = latinCats.find(c => getAge(c.category) === selectedAge && c.dance === dance);
                      return cat && (sel[cat.id]?.length || 0) > 0;
                    });
                    activeInnerKey = firstWithStars || innerTabs[0];
                  }
                  activeCat = latinCats.find(c => getAge(c.category) === selectedAge && c.dance === activeInnerKey);
                } else {
                  const selectedDisc = activeSidebarTab?.disc;
                  const discCats     = filtered.filter(c => c.discipline === selectedDisc);
                  innerTabs = STAR_AGE_ORDER.filter(age => discCats.some(c => getAge(c.category) === age));
                  if (!activeInnerKey || !innerTabs.includes(activeInnerKey)) {
                    // Jump to first age tab that has at least one star selected
                    const firstWithStars = innerTabs.find(age => {
                      const cat = discCats.find(c => getAge(c.category) === age);
                      return cat && (sel[cat.id]?.length || 0) > 0;
                    });
                    activeInnerKey = firstWithStars || innerTabs[0];
                  }
                  activeCat = discCats.find(c => getAge(c.category) === activeInnerKey);
                }

                // Count helpers
                const getSidebarCount = (tab) => {
                  if (tab.disc === "Latin") {
                    const age = tab.id.split("|")[1];
                    return latinCats
                      .filter(c => getAge(c.category) === age)
                      .reduce((s, c) => s + (sel[c.id]?.length || 0), 0);
                  }
                  return filtered
                    .filter(c => c.discipline === tab.disc)
                    .reduce((s, c) => s + (sel[c.id]?.length || 0), 0);
                };

                const getInnerCount = (key) => {
                  if (isLatinTab) {
                    const selectedAge = activeSidebarTab.id.split("|")[1];
                    const cat = latinCats.find(c => getAge(c.category) === selectedAge && c.dance === key);
                    return cat ? (sel[cat.id]?.length || 0) : 0;
                  }
                  const discCats = filtered.filter(c => c.discipline === activeSidebarTab?.disc);
                  const cat = discCats.find(c => getAge(c.category) === key);
                  return cat ? (sel[cat.id]?.length || 0) : 0;
                };

                const selectedForActive = activeCat ? (sel[activeCat.id] || []) : [];

                return (
                  <div className="stars-sidebar-layout">

                    {/* ── LEFT: Sidebar ── */}
                    <div className="stars-sidebar">

                      {/* Latin age tabs */}
                      {latinCats.length > 0 && (
                        <div className="stars-sidebar-group">
                          <div
                            className="stars-sidebar-disc-header"
                            style={{ color: STAR_DISCIPLINE_COLORS["Latin"].border, borderLeftColor: STAR_DISCIPLINE_COLORS["Latin"].border }}
                          >
                            Latin
                          </div>
                          {sidebarTabs.filter(t => t.disc === "Latin").map(tab => {
                            const count = getSidebarCount(tab);
                            const dcol  = STAR_DISCIPLINE_COLORS["Latin"];
                            return (
                              <button
                                key={tab.id}
                                className={`stars-sidebar-tab ${tab.id === activeSidebarId ? "active" : ""}`}
                                style={tab.id === activeSidebarId ? { borderLeftColor: dcol.border, background: dcol.bg } : {}}
                                onClick={() => { setStarsActiveTab(tab.id); setStarsInnerTab(null); }}
                              >
                                <span className="stars-tab-label">{tab.label}</span>
                                <span
                                  className={`stars-tab-count ${count > 0 ? "has-stars" : "no-stars"}`}
                                  style={count > 0 ? { background: dcol.border } : {}}
                                >
                                  {count}
                                </span>
                              </button>
                            );
                          })}
                        </div>
                      )}

                      {/* Non-Latin discipline tabs */}
                      {sidebarTabs.filter(t => t.disc !== "Latin").length > 0 && (
                        <div className="stars-sidebar-group">
                          {latinCats.length > 0 && <div className="stars-sidebar-separator" />}
                          {sidebarTabs.filter(t => t.disc !== "Latin").map(tab => {
                            const dcol  = STAR_DISCIPLINE_COLORS[tab.disc] || { bg: "#f5f5f5", border: "#999" };
                            const count = getSidebarCount(tab);
                            return (
                              <button
                                key={tab.id}
                                className={`stars-sidebar-tab ${tab.id === activeSidebarId ? "active" : ""}`}
                                style={tab.id === activeSidebarId ? { borderLeftColor: dcol.border, background: dcol.bg } : {}}
                                onClick={() => { setStarsActiveTab(tab.id); setStarsInnerTab(null); }}
                              >
                                <span className="stars-tab-label">{tab.label}</span>
                                <span
                                  className={`stars-tab-count ${count > 0 ? "has-stars" : "no-stars"}`}
                                  style={count > 0 ? { background: dcol.border } : {}}
                                >
                                  {count}
                                </span>
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>

                    {/* ── RIGHT: Content panel ── */}
                    <div className="stars-content">

                      {/* Content header */}
                      <div className="stars-content-header" style={{ borderBottomColor: color.border }}>
                        <span className="stars-content-disc" style={{ color: color.border }}>
                          {isLatinTab ? "Latin" : activeSidebarTab?.disc}
                        </span>
                        <span className="stars-content-cat">
                          {isLatinTab ? activeSidebarTab?.label : ""}
                        </span>
                      </div>

                      {/* Inner tabs: dances (Latin) or ages (non-Latin) */}
                      {innerTabs.length > 0 && (
                        <div className="stars-inner-tabs">
                          {innerTabs.map(key => {
                            const count = getInnerCount(key);
                            return (
                              <button
                                key={key}
                                className={`stars-inner-tab ${key === activeInnerKey ? "active" : ""}`}
                                style={key === activeInnerKey ? { borderBottomColor: color.border, color: color.border } : {}}
                                onClick={() => setStarsInnerTab(key)}
                              >
                                {key}
                                <span
                                  className="stars-inner-count"
                                  style={count > 0 ? { background: color.border, color: "#fff" } : {}}
                                >
                                  {count}
                                </span>
                              </button>
                            );
                          })}
                        </div>
                      )}

                      {/* View mode */}
                      {!starsEditMode && (
                        <div className="stars-content-list">
                          {selectedForActive.length > 0 ? (
                            selectedForActive.map(name => {
                              const rank = activeCat?.top3.findIndex(d => d.dancerName === name) ?? -1;
                              const dancer = activeCat?.top3[rank];
                              return (
                                <div key={name} className="stars-dancer-row">
                                  {rank >= 0 && rank < 3 && (
                                    <span className="stars-rank-icon" style={{ color: STAR_RANK_COLORS[rank] }}>
                                      {STAR_RANK_ICONS[rank]}
                                    </span>
                                  )}
                                  <DancerThumbnail dancerName={name} size={36} />
                                  <div className="stars-dancer-info">
                                    <button
                                      className="stars-dancer-link"
                                      onClick={() => {
                                        setStarsPopup({ show: false, competition: null });
                                        navigate(`/dancer/${encodeURIComponent(name)}`);
                                      }}
                                    >
                                      {name}
                                    </button>
                                    {dancer?.currentStudio && <span className="stars-studio">{dancer.currentStudio}</span>}
                                  </div>
                                  {dancer && <span className="stars-points">{Math.round(dancer.totalPoints).toLocaleString()} pts</span>}
                                </div>
                              );
                            })
                          ) : (
                            <div className="stars-content-empty">
                              <span>No stars selected for this category.</span>
                              {isAdmin && (
                                <button
                                  className="stars-quick-edit-btn"
                                  onClick={() => setStarsEditMode(true)}
                                  style={{ borderColor: color.border, color: color.border }}
                                >
                                  ✏️ Select stars
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      )}

                      {/* Edit mode (admin only) */}
                      {starsEditMode && (
                        <div className="stars-edit-list">
                          {activeCat ? (
                            <>
                              <p className="stars-edit-hint">Select who will enter from Round 2 in this category:</p>
                              {activeCat.top3.map((dancer, i) => {
                                const isChecked = (localStarSelections[activeCat.id] || []).includes(dancer.dancerName);
                                return (
                                  <label
                                    key={dancer.dancerName}
                                    className={`stars-edit-row ${isChecked ? "checked" : ""}`}
                                    style={isChecked ? { borderColor: color.border, background: color.bg } : {}}
                                  >
                                    <input
                                      type="checkbox"
                                      className="stars-edit-checkbox"
                                      checked={isChecked}
                                      onChange={() => toggleStarSelection(activeCat.id, dancer.dancerName)}
                                    />
                                    <span className="stars-rank-icon" style={{ color: STAR_RANK_COLORS[i] }}>{STAR_RANK_ICONS[i]}</span>
                                    <DancerThumbnail dancerName={dancer.dancerName} size={32} />
                                    <div className="stars-edit-dancer-info">
                                      <span className="stars-edit-name">{dancer.dancerName}</span>
                                      {dancer.currentStudio && <span className="stars-studio">{dancer.currentStudio}</span>}
                                    </div>
                                    <span className="stars-points">{Math.round(dancer.totalPoints).toLocaleString()} pts</span>
                                  </label>
                                );
                              })}
                            </>
                          ) : (
                            <div className="stars-content-empty">
                              <span>No ranking data available for this category.</span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}
            </div>

            {/* Footer */}
            <div className="stars-modal-footer">
              <p className="stars-disclaimer-note">
                The number of stars might change depending on the real time competitors per category - stars are advised to be ready to dance the first round if they are asked to
              </p>
              <div className="stars-footer-controls">
              <a href="/ranking" className="stars-modal-view-link" target="_blank" rel="noreferrer">
                View Full Rankings
              </a>
              <div style={{ display: "flex", gap: "0.5rem" }}>
                {starsEditMode && (
                  <button
                    className="stars-save-btn"
                    onClick={saveStarSelections}
                    disabled={starsSaving}
                  >
                    {starsSaving ? "Saving…" : "💾 Save"}
                  </button>
                )}
                <button
                  className="stars-modal-btn"
                  onClick={() => setStarsPopup({ show: false, competition: null })}
                >
                  Close
                </button>
              </div>
              </div>
            </div>

          </div>
        </div>
      , document.body)}

      {/* Free Latin Music Popup */}
      {showMusicPopup && (
        <div className="music-popup-overlay" onClick={() => setShowMusicPopup(false)}>
          <div className="music-popup-content" onClick={(e) => e.stopPropagation()}>
            <button 
              className="music-popup-close"
              onClick={() => setShowMusicPopup(false)}
            >
              ✕
            </button>
            <div className="music-popup-icon">
              🎵🎶🎵
            </div>
            <h2 className="music-popup-title">Free Latin Music</h2>
            <p className="music-popup-description">
              Listen or download free latin music from DJ Starseed<br />
              Follow for new music every week!
            </p>
            <a 
              href="https://t.me/dj_starseed"
              target="_blank"
              rel="noopener noreferrer"
              className="music-popup-button"
              onClick={async (e) => {
                // Track Telegram link click
                await trackButtonClick('music_banner', 'Free Latin Music', 'telegram_link', 'https://t.me/dj_starseed');
                logInteraction('music_telegram_click', { 
                  action: 'telegram_link_click',
                  destination: 'https://t.me/dj_starseed'
                });
                
                // Refresh stats for admin
                if (isAdmin) {
                  const allCompetitions = [...upcomingCompetitions, ...pastCompetitions];
                  const stats = await getAllClickStatsWithHistory(allCompetitions);
                  setClickStats(stats);
                }
              }}
            >
              🎧 Visit DJ Starseed on Telegram
              {isAdmin && clickStats['music_banner']?.['telegram_link'] && (
                <span className="admin-click-badge" style={{ marginLeft: '0.5rem' }}>
                  (👁️ {clickStats['music_banner']['telegram_link'].totalClicks || 0})
                </span>
              )}
            </a>
          </div>
        </div>
      )}

      {/* 🔮 Predict Winner */}
      {predictModal && (
        <PredictWinnerModal
          competition={predictModal}
          onClose={() => {
            setPredictModal(null);
            loadPredictionEvents();
          }}
        />
      )}
    </div>
  );
}